/**
 * Context-panel interaction cards — the data model + pure derivation.
 *
 * These are the "agent needs you / here's what happened" cards the right panel
 * renders ABOVE the Files and Apps &amp; tools sections. Every card here is folded
 * from REAL client state that reaches the panel:
 *   - the active thread's MorrowOS sub-agent roster (`MorrowAgent[]`) — its
 *     failed tool calls, its connect requests, and its final answers, and
 *   - the subject's live entitlements (seat allowance vs usage).
 *
 * There is no demo/scenario content: a card exists only when its real source
 * does. The card SHAPE is a superset that also covers agent-emitted scenarios
 * (decision / user-input / reply-draft / person-found …) so that when the
 * backend starts emitting those structured interactions the same renderer draws
 * them — but nothing here fabricates them.
 *
 * Pure (no React, no handlers). Buttons carry a declarative {@link ContextCardIntent}
 * the panel maps to the real handler (connect / upgrade / dismiss) at render time,
 * so this stays memoizable by input identity.
 */
import type { VariantProps } from "class-variance-authority";

import type { MorrowAgent } from "@t3tools/client-runtime/state/morrowAgentRuntime";

import { describeToolCall } from "../chat/workEntryStep";
import type { buttonVariants } from "../ui/button";

export type ContextCardTone = "info" | "warning" | "critical" | "success";

/** What a card's button actually does — mapped to the live handler by the panel. */
export type ContextCardIntent =
  | { readonly type: "dismiss" }
  | { readonly type: "connect"; readonly toolkit: string }
  | { readonly type: "upgrade"; readonly capability: string };

export interface ContextCardAction {
  readonly id: string;
  readonly label: string;
  readonly variant?: VariantProps<typeof buttonVariants>["variant"];
  /** Absent → the button is not rendered (never ship a button that does nothing). */
  readonly intent?: ContextCardIntent;
}

export interface ContextCardOption {
  readonly id: string;
  readonly label: string;
  readonly description?: string;
  readonly recommended?: boolean;
  readonly toolSlug?: string;
}

export interface ContextCardPerson {
  readonly name: string;
  readonly email: string;
  readonly role?: string;
  readonly domain?: string;
}

export interface ContextCardFile {
  readonly name: string;
  readonly mimeType?: string;
  readonly path?: string;
}

/**
 * `error` / `connection` / `seat-limit` / `task-summary` are LIVE (derived below).
 * The remaining kinds are the shapes an agent-emitted interaction would take;
 * they render through the same card but are only produced once a real source
 * emits them (none does client-side today).
 */
export type ContextCardKind =
  | "error"
  | "connection"
  | "seat-limit"
  | "task-summary"
  | "decision"
  | "user-input"
  | "reply-draft"
  | "approval"
  | "connection-recommended"
  | "token-expired"
  | "permission-needed"
  | "person-found"
  | "bulk-invite"
  | "new-member";

export interface ContextCard {
  readonly id: string;
  readonly kind: ContextCardKind;
  readonly tone: ContextCardTone;
  /** Composio toolkit slug — drives the app logo. */
  readonly toolSlug?: string;
  readonly title: string;
  readonly summary: string;
  readonly badge?: string;
  readonly progress?: { readonly value: number; readonly label: string };
  readonly listItemsLabel?: string;
  readonly listItems?: readonly string[];
  readonly detailLabel?: string;
  readonly detail?: string;
  readonly person?: ContextCardPerson;
  readonly file?: ContextCardFile;
  readonly options?: readonly ContextCardOption[];
  /** When true the options are pickable and a `confirm` action gates on a pick. */
  readonly selectableOptions?: boolean;
  readonly actions: readonly ContextCardAction[];
}

const TONE_PRIORITY: Record<ContextCardTone, number> = {
  critical: 0,
  warning: 1,
  info: 2,
  success: 3,
};

/** Clamp free text to a card-sized preview while keeping line breaks. */
function clampText(value: string, limit = 360): string {
  const trimmed = value.trim();
  if (trimmed.length <= limit) return trimmed;
  return `${trimmed.slice(0, limit - 1).trimEnd()}…`;
}

function titleCase(slug: string): string {
  const clean = slug.trim();
  if (!clean) return "App";
  return `${clean.charAt(0).toUpperCase()}${clean.slice(1)}`;
}

/** Distinct toolkits the roster asked the user to connect, in first-seen order. */
export function deriveConnectToolkits(agents: ReadonlyArray<MorrowAgent>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const agent of agents) {
    for (const raw of agent.connectRequests) {
      const slug = raw.trim().toLowerCase();
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      out.push(slug);
    }
  }
  return out;
}

/** One card per toolkit the agent is blocked on connecting. */
export function buildConnectionCard(
  toolkit: string,
  state: { readonly connecting: boolean; readonly connected: boolean },
): ContextCard {
  const desc = describeToolCall(toolkit);
  const appName = desc.appName ?? titleCase(toolkit);
  const label = state.connected
    ? "Connected"
    : state.connecting
      ? "Connecting…"
      : `Connect ${appName}`;
  return {
    id: `connect:${toolkit}`,
    kind: "connection",
    tone: state.connected ? "success" : "info",
    toolSlug: desc.slug ?? toolkit,
    title: `${appName} connection needed`,
    summary: state.connected
      ? `${appName} is connected — Morrow can pick up where it left off.`
      : `Morrow needs access to ${appName} to continue this task.`,
    actions: state.connected
      ? [{ id: "dismiss", label: "Dismiss", variant: "ghost", intent: { type: "dismiss" } }]
      : [
          { id: "skip", label: "Not now", variant: "ghost", intent: { type: "dismiss" } },
          ...(state.connecting
            ? []
            : [
                {
                  id: "connect",
                  label,
                  variant: "default" as const,
                  intent: { type: "connect" as const, toolkit },
                },
              ]),
        ],
  };
}

/**
 * A card for each recently-failed tool call (deduped by tool, newest first).
 * Offers a real "Reconnect" for calls whose tool resolves to a connectable app.
 */
export function deriveErrorCards(agents: ReadonlyArray<MorrowAgent>, limit = 3): ContextCard[] {
  type Failed = {
    readonly id: string;
    readonly toolName: string;
    readonly at: string;
    readonly result: unknown;
  };
  const failed: Failed[] = [];
  for (const agent of agents) {
    for (const call of agent.toolCalls) {
      if (call.status !== "failed") continue;
      failed.push({
        id: call.id,
        toolName: call.toolName,
        at: call.completedAt ?? call.startedAt,
        result: call.result,
      });
    }
  }
  failed.sort((a, b) => b.at.localeCompare(a.at));

  const seenTool = new Set<string>();
  const cards: ContextCard[] = [];
  for (const entry of failed) {
    const toolKey = entry.toolName.trim().toLowerCase();
    if (seenTool.has(toolKey)) continue;
    seenTool.add(toolKey);
    const desc = describeToolCall(entry.toolName);
    const appName = desc.appName ?? entry.toolName;
    const detail = typeof entry.result === "string" ? clampText(entry.result, 240) : null;
    cards.push({
      id: `error:${entry.id}`,
      kind: "error",
      tone: "critical",
      ...(desc.slug ? { toolSlug: desc.slug } : {}),
      title: `${appName} action failed`,
      summary: desc.appName
        ? `A ${appName} step didn't complete. Reconnect the app or try again.`
        : `The last tool call didn't complete.`,
      ...(detail ? { detailLabel: "Error", detail } : {}),
      actions: [
        { id: "dismiss", label: "Dismiss", variant: "ghost", intent: { type: "dismiss" } },
        ...(desc.slug
          ? [
              {
                id: "reconnect",
                label: `Reconnect ${appName}`,
                variant: "default" as const,
                intent: { type: "connect" as const, toolkit: desc.slug },
              },
            ]
          : []),
      ],
    });
    if (cards.length >= limit) break;
  }
  return cards;
}

/** Seat exhaustion → an "add seats" card wired to the real upgrade modal. */
export function deriveSeatLimitCard(input: {
  readonly isOrg: boolean;
  readonly used: number;
  readonly limit: number | null;
  readonly remaining: number | null;
}): ContextCard | null {
  const { isOrg, used, limit, remaining } = input;
  if (!isOrg || limit === null || limit <= 0) return null;
  if ((remaining ?? 0) > 0) return null;
  return {
    id: "seat-limit",
    kind: "seat-limit",
    tone: "warning",
    title: "Seat limit reached",
    summary: `All ${limit} seats on your plan are in use. Add seats to invite more people.`,
    progress: { value: 100, label: `${used} / ${limit} seats` },
    actions: [
      { id: "dismiss", label: "Dismiss", variant: "ghost", intent: { type: "dismiss" } },
      {
        id: "upgrade",
        label: "Add seats",
        variant: "default",
        intent: { type: "upgrade", capability: "seats" },
      },
    ],
  };
}

/** The most recent completed sub-agent's consolidated answer, as a summary card. */
export function deriveTaskSummaryCard(agents: ReadonlyArray<MorrowAgent>): ContextCard | null {
  let latest: MorrowAgent | null = null;
  for (const agent of agents) {
    if (agent.status !== "completed") continue;
    if (!agent.finalAnswer || agent.finalAnswer.trim().length === 0) continue;
    if (!latest || agent.updatedAt.localeCompare(latest.updatedAt) > 0) latest = agent;
  }
  if (!latest || !latest.finalAnswer) return null;
  const doneCalls = latest.toolCalls.filter((c) => c.status === "completed").length;
  return {
    id: `summary:${latest.turnId ?? "-"}:${latest.agentId}`,
    kind: "task-summary",
    tone: "success",
    title: latest.task ?? "Task complete",
    summary: "Morrow finished this task.",
    detailLabel: "Result",
    detail: clampText(latest.finalAnswer, 480),
    ...(doneCalls > 0
      ? { listItems: [`${doneCalls} tool call${doneCalls === 1 ? "" : "s"} completed`] }
      : {}),
    actions: [],
  };
}

/** Sort a heterogeneous card list by urgency (critical → success). */
export function sortContextCards(cards: readonly ContextCard[]): ContextCard[] {
  return [...cards].sort((a, b) => TONE_PRIORITY[a.tone] - TONE_PRIORITY[b.tone]);
}
